import React, { useState, useEffect, useContext } from "react";
import {
  Container,
  Table,
  Button,
  Form,
  Modal,
  Spinner,
  Alert,
  Badge,
  Card,
  Row,
  Col,
} from "react-bootstrap";
import { Navigate } from "react-router-dom";
import AuthContext from "../contexts/AuthContext";
import { toast, Toaster } from "react-hot-toast";
import api from "../api";

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("create"); // 'create', 'edit'
  const [currentUser, setCurrentUser] = useState({
    username: "",
    email: "",
    first_name: "",
    last_name: "",
    password: "",
    is_staff: false,
    is_superuser: false,
    profile: {
      can_edit: false,
      can_create: false,
    },
  });

  const { auth } = useContext(AuthContext);

  useEffect(() => {
    if (auth.user?.is_superuser) {
      fetchUsers();
    }
  }, [auth.user]);

  // Redirect if not superuser
  if (!auth.user?.is_superuser) {
    return <Navigate to="/" />;
  }

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await api.get("/api/users/");

      // Handle paginated response
      if (
        response.data &&
        response.data.results &&
        Array.isArray(response.data.results)
      ) {
        setUsers(response.data.results);
      } else if (response.data && Array.isArray(response.data)) {
        // Handle direct array response (in case pagination is disabled)
        setUsers(response.data);
      } else {
        setError("Unexpected response format from server");
      }
    } catch (err) {
      console.error("Error fetching users:", err);
      setError("Failed to load users. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name.includes("profile.")) {
      // Handle profile property changes
      const profileProp = name.split(".")[1];
      setCurrentUser({
        ...currentUser,
        profile: {
          ...currentUser.profile,
          [profileProp]: type === "checkbox" ? checked : value,
        },
      });
    } else {
      // Handle user property changes
      setCurrentUser({
        ...currentUser,
        [name]: type === "checkbox" ? checked : value,
      });
    }
  };

  const openCreateModal = () => {
    setCurrentUser({
      username: "",
      email: "",
      first_name: "",
      last_name: "",
      password: "",
      is_staff: false,
      is_superuser: false,
      profile: {
        can_edit: false,
        can_create: false,
      },
    });
    setModalMode("create");
    setShowModal(true);
  };

  const openEditModal = (user) => {
    // Clone the user to avoid modifying the original
    setCurrentUser({
      ...user,
      // Don't include password when editing
      password: "",
      // Ensure profile exists
      profile: user.profile || {
        can_edit: false,
        can_create: false,
      },
    });
    setModalMode("edit");
    setShowModal(true);
  };

  const handleSubmit = async () => {
    try {
      // Validate email format if provided
      if (currentUser.email && !isValidEmail(currentUser.email)) {
        toast.error(
          "Please enter a valid email address (e.g., user@example.com)"
        );
        return;
      }

      if (modalMode === "create") {
        // Create new user
        const response = await api.post("/api/users/register/", currentUser);
        toast.success(`User ${response.data.username} created successfully!`);
        fetchUsers();
      } else {
        // Edit existing user
        const userData = { ...currentUser };

        // Only include password if it was changed
        if (!userData.password) {
          delete userData.password;
        }

        // Ensure profile only contains fields that exist in the model
        if (userData.profile) {
          userData.profile = {
            can_edit: userData.profile.can_edit || false,
            can_create: userData.profile.can_create || false,
          };
        }

        console.log("Updating user with data:", userData);

        const response = await api.put(
          `/api/users/${currentUser.id}/`,
          userData
        );
        toast.success(`User ${response.data.username} updated successfully!`);
        fetchUsers();
      }

      setShowModal(false);
    } catch (err) {
      console.error("Error saving user:", err);
      const errorMessage =
        err.response?.data?.detail ||
        (err.response?.data
          ? JSON.stringify(err.response.data)
          : "Failed to save user. Please try again.");
      toast.error(errorMessage);
    }
  };

  // Helper function to validate email format
  const isValidEmail = (email) => {
    const re = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return re.test(String(email).toLowerCase());
  };

  const handleDeleteUser = async (userId, username) => {
    if (
      window.confirm(
        `Are you sure you want to delete user "${username}"? This action cannot be undone.`
      )
    ) {
      try {
        await api.delete(`/api/users/${userId}/`);
        toast.success(`User ${username} deleted successfully!`);
        fetchUsers();
      } catch (err) {
        console.error("Error deleting user:", err);
        toast.error(
          err.response?.data?.detail ||
            "Failed to delete user. Please try again."
        );
      }
    }
  };

  return (
    <Container className="my-4">
      <Toaster position="top-right" />
      <Row className="mb-4">
        <Col>
          <h2>User Management</h2>
          <p>Manage user accounts and permissions.</p>
        </Col>
        <Col xs="auto">
          <Button variant="primary" onClick={openCreateModal}>
            Create New User
          </Button>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" onClose={() => setError("")} dismissible>
          {error}
        </Alert>
      )}

      {loading ? (
        <div className="text-center my-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p className="mt-2">Loading users...</p>
        </div>
      ) : (
        <Card>
          <Card.Body>
            {users.length === 0 ? (
              <Alert variant="info">No users found.</Alert>
            ) : (
              <div className="table-responsive">
                <Table striped bordered hover>
                  <thead>
                    <tr>
                      <th>Username</th>
                      <th>Email</th>
                      <th>Name</th>
                      <th>Roles & Permissions</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => (
                      <tr key={user.id}>
                        <td>{user.username}</td>
                        <td>{user.email || "-"}</td>
                        <td>
                          {`${user.first_name || ""} ${
                            user.last_name || ""
                          }`.trim() || "-"}
                        </td>
                        <td>
                          {user.is_superuser && (
                            <Badge bg="danger" className="me-1">
                              Superuser
                            </Badge>
                          )}
                          {user.is_staff && (
                            <Badge bg="warning" className="me-1">
                              Staff
                            </Badge>
                          )}
                          {user.profile?.can_edit && (
                            <Badge bg="info" className="me-1">
                              Can Edit
                            </Badge>
                          )}
                          {user.profile?.can_create && (
                            <Badge bg="success" className="me-1">
                              Can Create
                            </Badge>
                          )}
                          {!user.is_superuser &&
                            !user.is_staff &&
                            !user.profile?.can_edit &&
                            !user.profile?.can_create && (
                              <Badge bg="secondary">View Only</Badge>
                            )}
                        </td>
                        <td>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            className="me-2"
                            onClick={() => openEditModal(user)}
                          >
                            Edit
                          </Button>
                          {/* Don't allow deleting self */}
                          {user.id !== auth.user.id && (
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() =>
                                handleDeleteUser(user.id, user.username)
                              }
                            >
                              Delete
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </Card.Body>
        </Card>
      )}

      {/* User Create/Edit Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {modalMode === "create"
              ? "Create New User"
              : `Edit User: ${currentUser.username}`}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Username</Form.Label>
                  <Form.Control
                    type="text"
                    name="username"
                    value={currentUser.username}
                    onChange={handleInputChange}
                    required
                    disabled={modalMode === "edit"} // Username shouldn't be editable
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={currentUser.email || ""}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>First Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="first_name"
                    value={currentUser.first_name || ""}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Last Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="last_name"
                    value={currentUser.last_name || ""}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>
                {modalMode === "create"
                  ? "Password"
                  : "Password (leave blank to keep current)"}
              </Form.Label>
              <Form.Control
                type="password"
                name="password"
                value={currentUser.password}
                onChange={handleInputChange}
                required={modalMode === "create"}
              />
            </Form.Group>

            <hr />
            <h5 className="mb-3">Roles and Permissions</h5>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    label="Staff Status"
                    name="is_staff"
                    checked={currentUser.is_staff}
                    onChange={handleInputChange}
                  />
                  <Form.Text className="text-muted">
                    Staff can access the admin panel but may have limited
                    permissions.
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    label="Superuser Status"
                    name="is_superuser"
                    checked={currentUser.is_superuser}
                    onChange={handleInputChange}
                  />
                  <Form.Text className="text-muted">
                    Superusers have full access to all system features and data.
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    label="Can Edit Data"
                    name="profile.can_edit"
                    checked={currentUser.profile?.can_edit}
                    onChange={handleInputChange}
                  />
                  <Form.Text className="text-muted">
                    User can edit existing data.
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    label="Can Create Data"
                    name="profile.can_create"
                    checked={currentUser.profile?.can_create}
                    onChange={handleInputChange}
                  />
                  <Form.Text className="text-muted">
                    User can upload CSV files and create new data.
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit}>
            {modalMode === "create" ? "Create User" : "Save Changes"}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default UserManagement;
